// ARCHX AI — client-side Smart Intake
// Drop ONE zip (or a pile of files) → auto-sort into the case categories with a
// confidence per file. Mirrors the engine classifier (engine/pipeline/smart_intake.py).
// Guardrail: never blind — low-confidence files surface YELLOW for a one-glance
// confirm, unrecognised files land in the manual tray.
import JSZip from 'jszip';

export const YELLOW_BELOW = 0.7;

// filename → { key: <UPLOAD_CATEGORIES key> | null, confidence, reason }
export function classifyName(name) {
  const n = (name || '').toLowerCase();
  const ext = n.includes('.') ? n.split('.').pop() : '';

  if (ext === 'csv') {
    if (n.includes('~upper~') || n.includes('~lower~') || n.includes('micron'))
      return { key: 'photogrammetry', confidence: 0.95, reason: 'photogrammetry CSV' };
    return { key: 'photogrammetry', confidence: 0.6, reason: 'CSV (assumed photogrammetry)' };
  }
  if (ext === 'dcm' || ext === 'dicom')
    return { key: 'cbct', confidence: 0.95, reason: 'DICOM / cone-beam slice' };
  if (['jpg', 'jpeg', 'png', 'heic', 'tif', 'tiff'].includes(ext))
    return { key: 'preop2d', confidence: 0.85, reason: 'pre-op photo' };
  if (ext === 'obj')
    return { key: 'preop2d', confidence: 0.7, reason: '3D facial scan' };

  if (ext === 'stl' || ext === 'ply') {
    if (/(wax|waxup|_cad|design)/.test(n)) {
      if (/(revision|_rev|additional)/.test(n)) return { key: 'revisions', confidence: 0.72, reason: 'design revision' };
      if (/(provisional|temp)/.test(n)) return { key: 'stlProvisional', confidence: 0.78, reason: 'temp provisional design' };
      if (/(aligned|exocad)/.test(n)) return { key: 'stlAligned', confidence: 0.72, reason: 'aligned / Exocad STL' };
      return { key: 'waxup', confidence: 0.85, reason: 'wax-up / design CAD' };
    }
    if (/(scancap|scan_cap|scancaps|post-surg|postsurg|_post)/.test(n)) return { key: 'postop', confidence: 0.8, reason: 'post-surgical IOScan w/ scan caps' };
    if (/(preop|pre-op|pre_op|preoperative)/.test(n)) return { key: 'preop3d', confidence: 0.82, reason: 'pre-op IOScan' };
    if (/(archtracer|fiducial|presurg|pre-surg)/.test(n)) return { key: 'surgical', confidence: 0.7, reason: 'pre-surgical scan w/ fiducials' };
    if (/(aligned|exocad)/.test(n)) return { key: 'stlAligned', confidence: 0.72, reason: 'aligned / Exocad STL' };
    if (/(tissue|bite)/.test(n)) return { key: 'postop', confidence: 0.55, reason: 'tissue scan (stage unclear)' };
    if (/(upperjaw|lowerjaw|jawscan|trios|itero|shining)/.test(n)) return { key: 'preop3d', confidence: 0.55, reason: 'IOScan (stage unclear)' };
    return { key: 'preop3d', confidence: 0.3, reason: 'STL — type unclear' };
  }
  if (ext === 'zip')                                  // a whole zip that survived expand() = a DICOM series
    return { key: 'cbct', confidence: 0.8, reason: 'CBCT DICOM series (zip)' };
  return { key: null, confidence: 0, reason: `.${ext || '?'} — unrecognised` };
}

// Expand a dropped list (files + zips) into flat {file, name} items. A zip that
// is mostly DICOM is kept whole (it IS the CBCT upload); other zips are unpacked.
async function expand(files) {
  const out = [];
  for (const f of files) {
    if (!f.name.toLowerCase().endsWith('.zip')) { out.push({ file: f, name: f.name }); continue; }
    try {
      const zip = await JSZip.loadAsync(f);
      const entries = Object.values(zip.files).filter(e => !e.dir);
      const dicomish = entries.filter(e => /\.(dcm|dicom)$/i.test(e.name) || !e.name.includes('.'));
      if (entries.length && dicomish.length / entries.length > 0.6) { out.push({ file: f, name: f.name }); continue; }
      for (const e of entries) {
        const base = e.name.split('/').pop();
        if (!base || base.startsWith('.') || e.name.includes('__MACOSX')) continue;
        const blob = await e.async('blob');
        out.push({ file: new File([blob], base, { type: blob.type }), name: base });
      }
    } catch {
      out.push({ file: f, name: f.name });           // unreadable zip → keep as-is
    }
  }
  return out;
}

// Sort dropped files/zips → { assignments: {key:[{file,confidence,reason}]}, unknown:[File], summary }
export async function smartSort(droppedFiles) {
  const items = await expand(Array.from(droppedFiles));
  const assignments = {};
  const unknown = [];
  for (const { file, name } of items) {
    const { key, confidence, reason } = classifyName(name);
    if (!key) { unknown.push(file); continue; }
    (assignments[key] = assignments[key] || []).push({ file, confidence, reason });
  }
  let sorted = 0, review = 0, cats = 0;
  Object.values(assignments).forEach(arr => { cats++; arr.forEach(a => { sorted++; if (a.confidence < YELLOW_BELOW) review++; }); });
  return { assignments, unknown, summary: { sorted, review, cats, unknown: unknown.length } };
}
